import {useMemo, useSyncExternalStore} from 'react';
import type {GroupCallState, RoomCallSummary} from '@phonehq/rtc-core';
import {groupCallService} from '../services/groupCallService';
import type {CallBlock, GroupCallUiState, IncomingGroupCall} from '../services/groupCallOrchestrator';
import {useGetTenantRtcFeaturesQuery} from '../services/groupCallFeatures';
import {isRtcRuntimeEnabled, resolveRtcFeatures, type RtcFeatureFlags} from '../services/rtcFeatureFlags';

export const useGroupCallState = (): GroupCallState =>
    useSyncExternalStore(groupCallService.call.subscribe, groupCallService.call.getSnapshot);

export const useRoomCalls = (): ReadonlyMap<string, RoomCallSummary> =>
    useSyncExternalStore(groupCallService.roomCalls.subscribe, groupCallService.roomCalls.getSnapshot);

export const useRoomCallSummary = (roomId: string | null | undefined): RoomCallSummary | undefined => {
    const calls = useRoomCalls();
    return roomId ? calls.get(roomId) : undefined;
};

export const useIncomingGroupCall = (): IncomingGroupCall | undefined =>
    useSyncExternalStore(groupCallService.incoming.subscribe, groupCallService.incoming.getSnapshot);

export const useGroupCallInvite = (): IncomingGroupCall | undefined =>
    useSyncExternalStore(groupCallService.invite.subscribe, groupCallService.invite.getSnapshot);

export const useCallBlock = (): CallBlock | undefined =>
    useSyncExternalStore(groupCallService.block.subscribe, groupCallService.block.getSnapshot);

export const useGroupCallUi = (): GroupCallUiState =>
    useSyncExternalStore(groupCallService.ui.subscribe, groupCallService.ui.getSnapshot);

export const useRtcFeatures = (): RtcFeatureFlags => {
    const {data} = useGetTenantRtcFeaturesQuery();
    const runtimeEnabled = isRtcRuntimeEnabled(window.__ENV__);
    return useMemo(() => resolveRtcFeatures(data, runtimeEnabled), [data, runtimeEnabled]);
};
